import React from 'react';
import PropTypes from 'prop-types';
import useExport from 'hook/useExport';
import { withTranslation } from 'react-i18next';
import {
  ButtonWithLoader,
  FormWrapper,
  FlexTableRow,
  FlexTableCol,
  Button,
} from 'virtues-bootstrap';

import { requestPatientReportPdf, requestProviderReportPdf } from 'services/api/request/exports';
import AdaptiveDataRow from 'components/DataBlock/AdaptiveDataRow';
import { ReactComponent as IconFileDownload } from 'svg/file-download-solid.svg';
import { ReactComponent as IconRemoveFile } from 'svg/trash.svg';
import { NOT_APPLICABLE } from 'constants/common';
import s from './styles.module.scss';

const contentSizes = {
  md: 12,
  lg: 8,
};

const titleSizes = {
  xs: 12,
  sm: 12,
  md: 12,
  lg: 4,
};

const DeviceTransmissionReport = ({
  attachmentLoading,
  attachmentUrl,
  deviceName,
  isRegisteredReport,
  handleDownloadDocument,
  handleRemoveReportClick,
  sentOn,
  markedReadByPatient,
  markedOpenByPatient,
  isResearcher,
  isTechnician,
  patientId,
  observationId,
  t,
  handleDownloadReport,
  transmissionType,
  unscheduledVisitReason,
  additionalDocuments,
  deviceClinicEmail,
}) => {
  const [patientPdfExport] = useExport(requestPatientReportPdf, patientId, observationId);
  const [providerPdfExport] = useExport(requestProviderReportPdf, patientId, observationId);

  return (
    <FormWrapper title={t('patient.deviceTransmissionReport')}>
      <AdaptiveDataRow title={t('reports.sentOn')}>{sentOn}</AdaptiveDataRow>
      {isResearcher && (
        <AdaptiveDataRow title={t('reports.markedReadOn')}>{markedReadByPatient}</AdaptiveDataRow>
      )}
      {isResearcher && (
        <AdaptiveDataRow title={t('reports.openedOn')}>{markedOpenByPatient}</AdaptiveDataRow>
      )}
      <AdaptiveDataRow title={t('reports.deviceVendor')}>{deviceName}</AdaptiveDataRow>
      <AdaptiveDataRow title={t('reports.transmissionType')}>{transmissionType}</AdaptiveDataRow>
      {unscheduledVisitReason !== NOT_APPLICABLE && unscheduledVisitReason !== '' && (
        <AdaptiveDataRow title={t('reports.unscheduledVisitReason')}>
          {unscheduledVisitReason}
        </AdaptiveDataRow>
      )}
      <AdaptiveDataRow title={t('reports.deviceClinicEmail')}>{deviceClinicEmail}</AdaptiveDataRow>
      <AdaptiveDataRow
        withLabelMargin
        titleSizes={titleSizes}
        contentSizes={contentSizes}
        title={t('reports.fullTransmissionReport')}
      >
        <div className={s.reportButtonGroup}>
          <ButtonWithLoader
            loading={attachmentLoading}
            outlined
            variant="primary"
            icon={<IconFileDownload />}
            onClick={handleDownloadReport({ attachmentUrl, patientId, deviceName, sentOn })}
          >
            {t('patient.downloadTransmissionReport')}
          </ButtonWithLoader>
          {isRegisteredReport && isTechnician && (
            <Button
              icon={<IconRemoveFile />}
              variant="primary"
              outlined
              onClick={handleRemoveReportClick}
            >
              {t('patient.removeTransmissionReport')}
            </Button>
          )}
        </div>
      </AdaptiveDataRow>
      {isResearcher && (
        <AdaptiveDataRow
          withLabelMargin
          titleSizes={titleSizes}
          contentSizes={contentSizes}
          title="Email Reports"
        >
          <div className={s.reportButtonGroup}>
            <Button
              icon={<IconFileDownload />}
              onClick={() => providerPdfExport(true)}
              variant="primary"
              outlined
            >
              Provider Report
            </Button>
            <Button
              icon={<IconFileDownload />}
              onClick={() => patientPdfExport(true)}
              variant="primary"
              outlined
            >
              Patient Report
            </Button>
          </div>
        </AdaptiveDataRow>
      )}
      {!!additionalDocuments.length && (
        <AdaptiveDataRow title={t('reports.additionalDocuments')}>
          {additionalDocuments.map(({ id, title }) => (
            <FlexTableRow key={id}>
              <FlexTableCol>{title}</FlexTableCol>
              <FlexTableCol>
                <Button
                  icon={<IconFileDownload />}
                  variant="link-secondary"
                  onClick={handleDownloadDocument(id)}
                >
                  {t('download')}
                </Button>
              </FlexTableCol>
            </FlexTableRow>
          ))}
        </AdaptiveDataRow>
      )}
    </FormWrapper>
  );
};

DeviceTransmissionReport.propTypes = {
  deviceClinicEmail: PropTypes.string.isRequired,
  attachmentUrl: PropTypes.string.isRequired,
  attachmentLoading: PropTypes.bool.isRequired,
  deviceName: PropTypes.string.isRequired,
  markedReadByPatient: PropTypes.string.isRequired,
  markedOpenByPatient: PropTypes.string.isRequired,
  isResearcher: PropTypes.bool.isRequired,
  isTechnician: PropTypes.bool.isRequired,
  isRegisteredReport: PropTypes.bool.isRequired,
  handleDownloadReport: PropTypes.func.isRequired,
  handleRemoveReportClick: PropTypes.func.isRequired,
  handleDownloadDocument: PropTypes.func.isRequired,
  patientId: PropTypes.string.isRequired,
  additionalDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
  ).isRequired,
  sentOn: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  transmissionType: PropTypes.string.isRequired,
  unscheduledVisitReason: PropTypes.string,
  observationId: PropTypes.string.isRequired,
};

DeviceTransmissionReport.defaultProps = {
  unscheduledVisitReason: false,
};

export default withTranslation()(DeviceTransmissionReport);
